import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';

import ROUTES from 'src/routes';

import { palette } from './palette';
import { Container, DrawLink, RevealOnScroll, Section, SectionHead } from './primitives';

export function PoweredExperienceSection(): JSX.Element {
  const { t } = useTranslation();

  return (
    <Section $alt>
      <Container>
        <SectionHead
          first={t('home.redesign.poweredFirst', 'Dutchie-powered')}
          rest={t('home.redesign.poweredRest', 'dispensary experience')}
          sub={t(
            'home.redesign.poweredSub',
            'The best dispensaries use technology that stays out of your way—so everything just works'
          )}
        />
        <RevealOnScroll>
          <Features>
            <FeatureCard>
              <FeatureIcon>✓</FeatureIcon>
              <h4>{t('home.redesign.featureMenusTitle', 'Menus that match the shelf')}</h4>
              <p>
                {t(
                  'home.redesign.featureMenusBody',
                  `What you see online is what's actually in stock. Inventory updates in real time, so you never show up to an "out of stock" surprise.`
                )}
              </p>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>
                <svg
                  width='19'
                  height='19'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='9' cy='20' r='1.5' />
                  <circle cx='18' cy='20' r='1.5' />
                  <path d='M3 4h2.2l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1L21 8H6' />
                </svg>
              </FeatureIcon>
              <h4>{t('home.redesign.featureCheckoutTitle', 'Checkout without the hassle')}</h4>
              <p>
                {t(
                  'home.redesign.featureCheckoutBody',
                  'Cashless payments, order-ahead pickup, and a checkout process designed to get you in and out. No app required.'
                )}
              </p>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>✦</FeatureIcon>
              <h4>{t('home.redesign.featureSmarterTitle', 'Smarter the more you visit')}</h4>
              <p>
                {t(
                  'home.redesign.featureSmarterBody',
                  'Personalized recommendations. Rewards that build automatically. Deals tailored to what you actually like.'
                )}
              </p>
            </FeatureCard>
          </Features>
        </RevealOnScroll>
        <LearnLink>
          <DrawLink href={ROUTES.DISPENSARY_B2B}>
            {t('home.redesign.learnHow', 'Learn how Dutchie helps dispensaries')} <span className='arr'>→</span>
          </DrawLink>
        </LearnLink>
      </Container>
    </Section>
  );
}

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${palette.bgCard};
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-4px);
  }

  h4 {
    margin: 0 0 14px;
    font-size: 22px;
    color: ${palette.cream};
  }

  p {
    margin: 0;
    color: ${palette.creamDim};
    font-size: 15px;
    line-height: 1.55;
  }
`;

const FeatureIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(233, 185, 73, 0.15);
  color: ${palette.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin: 0 auto 22px;
`;

const LearnLink = styled.div`
  text-align: center;
  margin-top: 40px;
`;
